import moment from "moment-timezone";
import { FavoriteStudent } from "services/favorites";
import { StudentStatus } from "services/student/model";
import { isActive, isSuspended } from "services/users";

/**
 * A student who has neither logged in nor had a lesson with this teacher for this
 * long is dormant. Teachers asked for the roster to stop being a list of everyone
 * they have ever been assigned; 90 days is the agreed line.
 */
export const QUIET_AFTER_DAYS = 90;

/** Students whose programme has ended. Kept out of the roster behind a disclosure. */
const PAST_STATUSES: StudentStatus[] = ["graduated", "pilot-completed"];

export type StudentGroupName = "active" | "covering" | "quiet" | "paused" | "past";

/**
 * Display order. "quiet" deliberately outranks "covering" when assigning a student to
 * a group: the point is that everything dormant collapses into one place regardless of
 * whose student it is. Covering rows are marked individually so the relationship is
 * still visible.
 */
export const STUDENT_GROUP_ORDER: StudentGroupName[] = [
  "active",
  "covering",
  "quiet",
  "paused",
];

export const STUDENT_GROUP_TITLES: Record<StudentGroupName, string> = {
  active: "Active",
  covering: "Covering for",
  quiet: `Quiet ${QUIET_AFTER_DAYS}d+`,
  paused: "Paused",
  past: "Past students",
};

type GroupStudentsArgs = {
  students: FavoriteStudent[];
  instructorId: string;
  /** studentId -> ISO date of their most recent completed lesson with this teacher. */
  lastLessonByStudent?: Record<string, string>;
};

export function groupStudents(args: GroupStudentsArgs) {
  const groups: Record<StudentGroupName, FavoriteStudent[]> = {
    active: [],
    covering: [],
    quiet: [],
    paused: [],
    past: [],
  };
  for (const student of args.students) {
    if (!isBrowsable(student)) {
      continue;
    }
    groups[groupOf(student, args.instructorId, args.lastLessonByStudent)].push(student);
  }
  return groups;
}

function groupOf(
  student: FavoriteStudent,
  instructorId: string,
  lastLessonByStudent?: Record<string, string>
): StudentGroupName {
  if (PAST_STATUSES.includes(student.student.status)) {
    return "past";
  }
  if (student.student.status === "paused") {
    return "paused";
  }
  if (isQuiet(student, lastLessonByStudent)) {
    return "quiet";
  }
  if (!isPrimaryInstructor(student, instructorId)) {
    return "covering";
  }
  return "active";
}

/**
 * Suspended accounts still belong in the roster; anything else the API may return
 * (deleted, pending) does not.
 */
function isBrowsable(student: FavoriteStudent) {
  return isActive(student.status) || isSuspended(student.status);
}

export function isPrimaryInstructor(student: FavoriteStudent, instructorId: string) {
  return student.primary_instructor?.id === instructorId;
}

/**
 * Whether a recent lesson could change anyone's grouping.
 *
 * The lesson lookup is by far the heaviest request the roster makes, and it only ever
 * rescues a student who already looks dormant by last_seen alone. A teacher whose
 * students have all been active recently gets the same roster without it, so it is not
 * worth fetching for them.
 */
export function needsLastLessonLookup(students: FavoriteStudent[]) {
  return students.some(
    student =>
      isBrowsable(student) &&
      !PAST_STATUSES.includes(student.student.status) &&
      student.student.status !== "paused" &&
      isQuiet(student)
  );
}

export function isQuiet(
  student: FavoriteStudent,
  lastLessonByStudent?: Record<string, string>
) {
  const days = daysSinceEngagement(student, lastLessonByStudent);
  return days !== null && days >= QUIET_AFTER_DAYS;
}

/**
 * Days since the student last did anything: logged in, or had a lesson. Either signal
 * counts, because a student taught every week who never opens the app is not dormant
 * and last_seen alone would say otherwise. Null when neither signal exists.
 */
export function daysSinceEngagement(
  student: FavoriteStudent,
  lastLessonByStudent?: Record<string, string>
) {
  const candidates = [student.last_seen, lastLessonByStudent?.[student.id]]
    .filter((value): value is string => Boolean(value))
    .map(value => moment.utc().diff(moment.utc(value), "days"));
  return candidates.length ? Math.min(...candidates) : null;
}
